import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "sonner";
import {
  callToast,
  showErrorMessages,
} from "../../../src/services/utilityFunctions.js";
import { useGlobalState } from "../../../src/services/hostelConst";
import { getLocalStorageData } from "../../services/axiosComponent";
import { selectStyle } from "../../services/constant";
import {
  getTutorList,
  getSubjectList,
  getDivisionSectionList,
  getClassesList,
  getStudentList,
  createTutorAssignment,
  updateTutorAssignment,
  getDivisionSectionLectureList,
  getTutorAssignmentDetails,
} from "../../services/Academic-Services.js";
import { closeModal } from "../../services/redux/reducers/modalSlice";
import { useDispatch, useSelector } from "react-redux";

const AddAssignmentForm = ({ navigate, refresh, params }) => {
  const modal = useSelector((state) => state.modal);
  const sectionDetails = useSelector((state) => state.section);
  const dispatch = useDispatch();

  // States for loading indicators
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(modal.isEditing);
  const [isTutorOptionsLoading, setIsTutorOptionsLoading] = useState(false);
  const [isSubjectOptionsLoading, setIsSubjectOptionsLoading] = useState(false);
  const [isSectionOptionsLoading, setIsSectionOptionsLoading] = useState(false);
  const [isClassOptionsLoading, setIsClassOptionsLoading] = useState(false);
  const [isStudentOptionsLoading, setIsStudentOptionsLoading] = useState(false);
  const [assigneeId, setAssigneeId] = useState(null);
  // Options for select fields
  const [tutorOptions, setTutorOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);

  // Submission type options
  const submissionTypeOptions = [
    { value: "ONLINE", label: "Online" },
    { value: "OFFLINE", label: "Offline" },
  ];

  // Assignee type options
  const assigneeTypeOptions = [
    { value: "CLASS", label: "Class" },
    { value: "SECTION", label: "Section" },
    { value: "INDIVIDUAL", label: "Individual" },
  ];

  // Form validation
  const [errors, setErrors] = useState({});

  // Initial form data setup
  const prepareFormData = (data = null) => {
    return {
      name: data != null ? data.NAME : "",
      description: data != null ? data.DESCRIPTION : "",
      reference: data != null ? data.REFERENCE : "",
      score: data != null ? data.SCORE : "",
      tutor: {
        value: data != null ? data._TUTOR : "1350448852723101696",
        label: data != null ? data.TUTOR : "",
      },
      subject: {
        value: data != null ? data._SUBJECT : "",
        label: data != null ? data.SUBJECT : "",
        _CLASS: "",
        _SECTION: "",
        CLASS: "",
        SECTION: "",
      },
      section: {
        value: data != null ? data._SECTION : "",
        label: data != null ? data.SECTION : "",
      },
      submissionType: {
        value: data != null ? data.SUBMISSION_TYPE : "ONLINE",
        label:
          data != null
            ? data.SUBMISSION_TYPE === "ONLINE"
              ? "Online"
              : "Offline"
            : "Online",
      },
      assigneeType: {
        value: data != null ? data.ASSIGNEE_TYPE : "",
        label: data != null ? data.ASSIGNEE_TYPE : "",
      },
      class: {
        value: data != null ? data._CLASS : "",
        label: data != null ? data.CLASS : "",
      },
      student: {
        value: data != null ? data._STUDENT : "",
        label: data != null ? data.STUDENT : "",
      },
    };
  };

  const [formData, setFormData] = useState(prepareFormData());

  // Fetch existing entity data if editing
  const getEntityDetails = async (entityId) => {
    setLoadingData(true);

    // Replace with your actual entity details API call
    var res = await getTutorAssignmentDetails(entityId);
    if (res.status == "success") {
      console.log(res, "get entity details");
      setFormData(prepareFormData(res.data));
    } else {
      showErrorMessages({ data: res, navigate });
    }
    setLoadingData(false);
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setErrors({});
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Reset dependent fields when assignee type changes
  };

  // Form validation
  const validateFields = () => {
    let isValid = true;
    let errors = {};

    if (formData.name === "") {
      isValid = false;
      errors.name = "Name is required";
    }

    if (formData.description === "") {
      isValid = false;
      errors.description = "Description is required";
    }

    if (formData.score === "") {
      isValid = false;
      errors.score = "Score is required";
    } else if (isNaN(formData.score) || parseFloat(formData.score) < 0) {
      isValid = false;
      errors.score = "Score must be a valid positive number";
    }

    if (formData.tutor.value === "") {
      isValid = false;
      errors.tutor = "Tutor is required";
    }

    if (formData.subject.value === "") {
      isValid = false;
      errors.subject = "Subject is required";
    }

    if (formData.submissionType.value === "") {
      isValid = false;
      errors.submissionType = "Submission type is required";
    }

    if (formData.assigneeType.value === "") {
      isValid = false;
      errors.assigneeType = "Assignee type is required";
    } else {
      // Validate based on assignee type
      if (
        formData.assigneeType.value === "CLASS" &&
        formData.class.value === ""
      ) {
        isValid = false;
        errors.class = "Class is required";
      } else if (
        formData.assigneeType.value === "SECTION" &&
        formData.section.value === ""
      ) {
        isValid = false;
        errors.section = "Section is required";
      } else if (
        formData.assigneeType.value === "INDIVIDUAL" &&
        formData.student.value === ""
      ) {
        isValid = false;
        errors.student = "Student is required";
      }
    }

    setErrors(errors);
    return isValid;
  };

  // Form submission
  const handleSubmit = async () => {
    if (validateFields()) {
      setLoading(true);

      // Prepare submission data
      const submitData = {
        name: formData.name,
        description: formData.description,
        reference: formData.reference,
        assignmentScore: formData.score,
        tutorId: formData.tutor.value,
        subjectId: formData.subject.value,
        submissionType: formData.submissionType.value,
        assigneeType: formData.assigneeType.value,
        assigneeId,
        regionId: formData.subject._REGION,
        siteId: formData.subject._SITE,
        organizationId:
          getLocalStorageData("profileDetails")?.["_ORGANIZATION_"]?._ID,
        id: modal.isEditing ? modal.data._ID : null,
      };

      // Add appropriate IDs based on assignee type

      console.log(submitData, "submitData");

      // Replace with your actual API calls
      var res = !modal.isEditing
        ? await createTutorAssignment(submitData)
        : await updateTutorAssignment(submitData);
      if (res.status == "success") {
        callToast(
          "success",
          modal.isEditing ? "Updated" : "Created",
          `Assignment ${modal.isEditing ? "updated" : "created"} successfully`
        );

        await refresh({ columnRefresh: false, isLoading: false });

        dispatch(closeModal());
      } else {
        showErrorMessages({ data: res, navigate });
      }
      setLoading(false);
    }
  };

  // Fetch tutors
  const getTutorListOptions = async (query = "") => {
    setIsTutorOptionsLoading(true);
    var res = await getTutorList({
      query: query,
      status: "Y",
      regionId: sectionDetails._REGION,
      siteId: sectionDetails._SITE,
      sectionId: sectionDetails._ID,
    });
    if (res.status == "success") {
      var list = [];
      res.data.entityList.map((item) => {
        list.push({
          value: item._ID,
          label: item.NAME,
          _REGION: item._REGION,
          _SITE: item._SITE,
        });
      });
      setTutorOptions(list);
    } else {
      showErrorMessages({ data: res, navigate });
    }
    setIsTutorOptionsLoading(false);
  };

  // Fetch subjects
  const getSubjectListOptions = async (query = "") => {
    setIsSubjectOptionsLoading(true);
    var res = await getDivisionSectionLectureList({
      query: query,
      tutorId: formData.tutor.value,
      status: "Y",
    });

    if (res.status == "success") {
      console.log("called getSubjectOptions" + res.data);
      console.log(res.data);
      var list = [];
      res.data.entityList.map((item) => {
        list.push({
          value: item._SUBJECT,
          label: item.SUBJECT,
          _REGION: item._REGION,
          _SITE: item._SITE,
          SECTION: item.SECTION,
          _SECTION: item._SECTION,
          CLASS: item.CLASS,
          _CLASS: item._CLASS,
        });
      });
      setSubjectOptions(list);
    } else {
      showErrorMessages({ data: res, navigate });
    }
    setIsSubjectOptionsLoading(false);
  };

  // Fetch sections
  const getSectionListOptions = async (query = "") => {
    setIsSectionOptionsLoading(true);
    var res = await getDivisionSectionList({
      query: query,
      status: "Y",
    });
    if (res.status == "success") {
      var list = [];
      res.data.entityList.map((item) => {
        list.push({ value: item._ID, label: item.NAME });
      });
      setSectionOptions(list);
    } else {
      showErrorMessages({ data: res, navigate });
    }
    setIsSectionOptionsLoading(false);
  };

  // Fetch classes
  const getClassListOptions = async (query = "") => {
    setIsClassOptionsLoading(true);
    var res = await getClassesList({
      query: query,
      status: "Y",
      regionId: sectionDetails._REGION,
      siteId: sectionDetails._SITE,
    });
    if (res.status == "success") {
      var list = [];
      res.data.entityList.map((item) => {
        list.push({ value: item._ID, label: item.NAME });
      });
      setClassOptions(list);
    } else {
      showErrorMessages({ data: res, navigate });
    }
    setIsClassOptionsLoading(false);
  };

  // Fetch students
  const getStudentListOptions = async (query = "", classId) => {
    setIsStudentOptionsLoading(true);

    var res = await getStudentList({
      query: query,
      status: "Y",
      classId: classId == null ? formData.class.value : classId,
    });
    console.log(res, "getStudentListOptions");

    if (res.status == "success") {
      var list = [];
      res.data.entityList.map((item) => {
        list.push({ value: item._ID, label: item.NAME });
      });
      setStudentOptions(list);
    } else {
      showErrorMessages({ data: res, navigate });
    }
    setIsStudentOptionsLoading(false);
  };

  useEffect(() => {
    // Initial data loading
    // getTutorListOptions();
    getSubjectListOptions();

    // Only load these options if editing and there's existing data with appropriate assignee type
    if (modal.isEditing) {
      getEntityDetails(modal.data._ID);
    }

    return () => {};
  }, []);

  // Load appropriate options based on assignee type
  useEffect(() => {
    if (formData.assigneeType.value === "INDIVIDUAL") {
      if (formData.subject.value == "" || formData.section.value == null) {
        setErrors({ ...errors, student: "select the Subject first" });
      } else {
        getStudentListOptions();
      }
    }
    if (formData.assigneeType.value === "CLASS") {
      if (formData.subject.value == "" || formData.section.value == null) {
        setErrors({ ...errors, class: "select the Subject first" });
      }
    }
    if (formData.assigneeType.value === "SECTION") {
      if (formData.subject.value == "" || formData.section.value == null) {
        setErrors({ ...errors, section: "select the Subject first" });
      }
    }
  }, [formData.assigneeType.value]);

  return (
    <>
      {loadingData ? (
        <center>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </center>
      ) : (
        <section className="login-content">
          <div className="">
            <div className="d-flex">
              <div className="w-100 ps-4 pe-4">
                <form>
                  <div className="mb-3">
                    <div className="form-row">
                      <div className="col">
                        <label className="form-label">Name</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter name"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                        />
                        {errors.name && (
                          <small className="text-danger">{errors.name}</small>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="form-row">
                      <div className="col">
                        <label className="form-label">Description</label>
                        <input
                          className="form-control"
                          placeholder="Enter description"
                          value={formData.description}
                          onChange={(e) =>
                            handleChange("description", e.target.value)
                          }
                        />
                        {errors.description && (
                          <small className="text-danger">
                            {errors.description}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="form-row">
                      <div className="col">
                        <label className="form-label  no-asterisk">
                          Reference
                        </label>
                        <input
                          className="form-control"
                          placeholder="Enter reference"
                          value={formData.reference}
                          onChange={(e) =>
                            handleChange("reference", e.target.value)
                          }
                        />
                        {errors.reference && (
                          <small className="text-danger">
                            {errors.reference}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Subject</label>
                    <Select
                      styles={selectStyle}
                      options={subjectOptions}
                      value={
                        formData.subject.label === "" ? null : formData.subject
                      }
                      components={
                        isSubjectOptionsLoading ? { DropdownIndicator } : null
                      }
                      onChange={(newValue) => {
                        handleChange(
                          "subject",
                          newValue == null ? { value: "", label: "" } : newValue
                        );
                        if (newValue != null) {
                          handleChange("section", {
                            value: newValue._SECTION,
                            label: newValue.SECTION,
                          });
                          handleChange("class", {
                            value: newValue._CLASS,
                            label: newValue.CLASS,
                          });
                          console.log("classId here", newValue._CLASS);
                          getStudentListOptions("", newValue._CLASS);
                        }
                      }}
                      onInputChange={(inputValue, { action }) => {
                        if (action === "input-change") {
                          getSubjectListOptions(inputValue);
                        }
                      }}
                      noOptionsMessage={() => "No subjects available"}
                      isSearchable={true}
                      placeholder="Select Subject"
                      className="basic-single"
                      classNamePrefix="select"
                    />
                    {errors.subject && (
                      <small className="text-danger">{errors.subject}</small>
                    )}
                  </div>

                  <div className="mb-3">
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">Submission Type</label>
                        <Select
                          styles={selectStyle}
                          options={submissionTypeOptions}
                          value={
                            formData.submissionType.label === ""
                              ? null
                              : formData.submissionType
                          }
                          onChange={(newValue) => {
                            handleChange(
                              "submissionType",
                              newValue == null
                                ? { value: "", label: "" }
                                : newValue
                            );
                          }}
                          isSearchable={false}
                          placeholder="Select Submission Type"
                          className="basic-single"
                          classNamePrefix="select"
                        />
                        {errors.submissionType && (
                          <small className="text-danger">
                            {errors.submissionType}
                          </small>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Assignment Score</label>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          placeholder="Enter assignment score"
                          value={formData.score}
                          onChange={(e) =>
                            handleChange("score", e.target.value)
                          }
                        />
                        {errors.score && (
                          <small className="text-danger">{errors.score}</small>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Assignee Type</label>
                    <Select
                      styles={selectStyle}
                      options={assigneeTypeOptions}
                      value={
                        formData.assigneeType.label === ""
                          ? null
                          : formData.assigneeType
                      }
                      onChange={(newValue) => {
                        handleChange(
                          "assigneeType",
                          newValue == null ? { value: "", label: "" } : newValue
                        );
                        if (newValue != null) {
                          var assigneeID = null;
                          if (newValue.value == "CLASS") {
                            assigneeID = formData.class.value;
                          } else if (newValue.value == "SECTION") {
                            assigneeID = formData.section.value;
                          }

                          setAssigneeId(assigneeID);
                        }
                      }}
                      isSearchable={false}
                      placeholder="Select Assignee Type"
                      className="basic-single"
                      classNamePrefix="select"
                    />
                    {errors.assigneeType && (
                      <small className="text-danger">
                        {errors.assigneeType}
                      </small>
                    )}
                  </div>

                  {/* Conditional fields based on assignee type with animation */}
                  <div
                    className="dynamic-field-container"
                    style={{
                      overflow: "",
                      transition:
                        "max-height 0.5s ease-in-out, opacity 0.5s ease-in-out",
                      maxHeight: formData.assigneeType.value ? "200px" : "0",
                      opacity: formData.assigneeType.value ? 1 : 0,
                    }}
                  >
                    {formData.assigneeType.value === "CLASS" && (
                      <div className="mb-3 dynamic-field">
                        <label className="form-label">Class</label>
                        <Select
                          isDisabled={true}
                          isSearchable={false}
                          styles={selectStyle}
                          options={classOptions}
                          value={
                            formData.class.label === "" ? null : formData.class
                          }
                          components={
                            isClassOptionsLoading ? { DropdownIndicator } : null
                          }
                          onChange={(newValue) => {
                            setAssigneeId(newValue.value);
                            handleChange(
                              "class",
                              newValue == null
                                ? { value: "", label: "" }
                                : newValue
                            );
                          }}
                          onInputChange={(inputValue, { action }) => {
                            if (action === "input-change") {
                              getClassListOptions(inputValue);
                            }
                          }}
                          noOptionsMessage={() => "No classes available"}
                          placeholder="Select Class"
                          className="basic-single"
                          classNamePrefix="select"
                        />
                        {errors.class && (
                          <small className="text-danger">{errors.class}</small>
                        )}
                      </div>
                    )}

                    {formData.assigneeType.value === "SECTION" && (
                      <div className="mb-3 dynamic-field">
                        <label className="form-label">Section</label>
                        <Select
                          styles={selectStyle}
                          isDisabled={true}
                          isSearchable={false}
                          options={sectionOptions}
                          value={
                            formData.section.label === ""
                              ? null
                              : formData.section
                          }
                          components={
                            isSectionOptionsLoading
                              ? { DropdownIndicator }
                              : null
                          }
                          onChange={(newValue) => {
                            setAssigneeId(newValue.value);
                            handleChange(
                              "section",
                              newValue == null
                                ? { value: "", label: "" }
                                : newValue
                            );
                          }}
                          onInputChange={(inputValue, { action }) => {
                            if (action === "input-change") {
                              getSectionListOptions(inputValue);
                            }
                          }}
                          noOptionsMessage={() => "No sections available"}
                          placeholder="Select Section"
                          className="basic-single"
                          classNamePrefix="select"
                        />
                        {errors.section && (
                          <small className="text-danger">
                            {errors.section}
                          </small>
                        )}
                      </div>
                    )}

                    {formData.assigneeType.value === "INDIVIDUAL" && (
                      <div className="mb-3 dynamic-field">
                        <label className="form-label">Student</label>
                        <Select
                          isDisabled={
                            formData.subject.value == "" ||
                            formData.section.value == null
                          }
                          styles={selectStyle}
                          options={studentOptions}
                          value={
                            formData.student.label === ""
                              ? null
                              : formData.student
                          }
                          components={
                            isStudentOptionsLoading
                              ? { DropdownIndicator }
                              : null
                          }
                          onChange={(newValue) => {
                            setAssigneeId(newValue.value);
                            handleChange(
                              "student",
                              newValue == null
                                ? { value: "", label: "" }
                                : newValue
                            );
                          }}
                          onInputChange={(inputValue, { action }) => {
                            if (action === "input-change") {
                              getStudentListOptions(inputValue);
                            }
                          }}
                          noOptionsMessage={() => "No students available"}
                          isSearchable={true}
                          placeholder="Select Student"
                          className="basic-single"
                          classNamePrefix="select"
                        />
                        {errors.student && (
                          <small className="text-danger">
                            {errors.student}
                          </small>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-end mt-4 gap-2">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      onClick={() => dispatch(closeModal())}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <div
                          className="spinner-border text-light spinner-border-sm"
                          role="status"
                        >
                          <span className="sr-only">Loading...</span>
                        </div>
                      ) : modal.isEditing ? (
                        "Update"
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

// Loading indicator for Select components
const DropdownIndicator = (props) => {
  return (
    <div {...props.innerProps}>
      <div
        className="spinner-border text-primary spinner-border-sm m-2"
        role="status"
      >
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
};

export default AddAssignmentForm;
